import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { Helmet } from 'react-helmet';
import WithLayout from '../WithLayout';
import { queryService } from '../../domain/services/queryService';
import { Broadsheet } from '../../components/Paragraph/Broadsheet';
import { BannerModule } from '../../components/Paragraph/BannerModule';
import { PhotoOfTheDayParagraph } from '../../components/Paragraph/PhotoOfTheDayParagraph';
import { FocusedHeroPackage } from '../../components/Paragraph/FocusedHeroPackage';
import { ContentPackage } from '../../components/Paragraph/ContentPackage';
import { LeftAndRightContentPackage } from '../../components/Paragraph/LeftAndRightContentPackage';
import { CallToActionButton } from '../../components/Paragraph/CallToActionButton';
import { TvGuide } from '../../components/Paragraph/TvGuide';
import TermView from '../../components/HubPage/TermView';
import { InfiniteScroll } from '../../components/HubPage/InfiniteScroll';
import { ContentHtmlPackage } from '../../components/Paragraph/ContentHtmlPackage';
import { PromoParagraph } from '../../components/Paragraph/PromoParagraph';
import { PromoBannerParagraph } from '../../components/Paragraph/PromoBannerParagraph';
import { AmbientPlayerModule } from '../../components/Paragraph/AmbientPlayerModule';
import { SponsoredBar } from '../../components/SponsoredBar';
import { SponsoredAdditionalText } from '../../components/SponsoredAdditionalText';
import { StandaloneVideo } from '../../components/Paragraph/StandaloneVideo';
import { ImmersiveLead } from '../../components/Paragraph/ImmersiveLead';
import { InlineText } from '../../components/Paragraph/InlineText';
// import { calculatePageTitle } from './utils/calculatePageTitle';
import { isPaginationValid } from './utils/isPaginationValid';
import { Sweepstakes } from '../../components/responsive/atoms/Sweepstakes';
import { RelatedHubPages } from '../../components/Paragraph/RelatedHubPages';
import { NewsletterModuleParagraph } from '../../components/Paragraph/NewsletterModuleParagraph';
import { AdInsert } from '../../components/responsive/atoms/Ad/AdInsert';
import { vidLanding } from '../../components/Styles';
import { PromoCarousel } from '../../components/Paragraph/PromoCarousel';
import { VideoPlaylist } from '../../components/Paragraph/VideoPlaylist';
import { ImageCarousel } from '../../components/Paragraph/ImageCarousel';
import { OneUp } from '../../components/Paragraph/OneUp';
import { Carousel } from '../../components/Paragraph/Carousel';
import { CardStack } from '../../components/Paragraph/CardStack';
import { getColors } from '../../domain/utils/colorUtils';
import { PreHeadline } from '../../components/responsive/atoms/PreHeadline';
import { ContainerMain } from '../../components/responsive/atoms/ContainerMain';
import { conf, t } from '../../domain/services/configService';
import { theme } from '../../domain/theme';
import { PromoGridParagraph } from '../../components/Paragraph/PromoGridParagraph';
import { TravelGuideBreadcrumbsParagraph } from '../../components/Paragraph/TravelGuideBreadcrumbsParagraph';
import { TravelGuideCountryListParagraph } from '../../components/Paragraph/TravelGuideCountryListParagraph';
import { TravelGuideSearchBoxParagraph } from '../../components/Paragraph/TravelGuideSearchBoxParagraph';

const nodeTypeHubPage = css`
  article& > div.node__cont > div {
    clear: both;
  }

  & .card__deck {
    display: none !important;
  }
`;

const getPageFromQuery = () =>
  parseInt(queryService.getParameter('page') || 1, 10);

const HubPage = ({ content }) => {
  const articleTag = useRef(null);
  const [firstPageReached, setFirstPageReached] = useState(false);
  const [currentPage, setCurrentPage] = useState(getPageFromQuery);
  const pageFromFirstRender = useRef(getPageFromQuery()).current;

  const memoizedSetCurrentPage = useCallback(setCurrentPage);
  const showFirstPage =
    pageFromFirstRender === 1 || currentPage === 1 || firstPageReached;

  useEffect(() => {
    window.addEventListener(
      'hub-page-first-page-reached',
      () => setFirstPageReached(true),
      {
        once: true
      }
    );

    // Infinite scroll best practices (@see https://developers.google.com/search/blog/2014/02/infinite-scroll-search-friendly)
    // indicate that if a user request an invalid pagination, a 404 error
    // should be returned. We cannot return it from the server, so instead
    // we redirect to a URL that will respond with that 404.
    // Disclaimer: this is not the best approach since it doesn't return
    // a 404 code from the server, but the product owner asked for it.
    if (currentPage > 1 && !isPaginationValid(content, currentPage)) {
      document.location = '/error';
    }
  }, []);
  const colors = getColors(content.backgroundColor);

  const styles = {
    explorers: css`
      margin-top: ${theme.spacing('xs')};
    `,
    main: css`
      min-height: 2000px;
      ${content.backgroundColor
        ? `background-color: ${colors.background}; color:${colors.color};`
        : ''}
    `,
    firstPageContent: css`
      display: block ${firstPageReached && '!important'};
    `
  };

  const prevPage = parseInt(currentPage, 10) - 1;
  const nextPage = parseInt(currentPage, 10) + 1;
  const contentUrlPath = conf.isStage
    ? `http://fng-globalsites-stage-html-current-${conf.siteCountryCode}.s3-website.eu-central-1.amazonaws.com${content.url?.path}`
    : `https://${conf.canonicalDomain}${content.url?.path}`;
  const navigationMeta = {
    prev:
      currentPage > 1 ? (
        <link rel="prev" href={`${contentUrlPath}?page=${prevPage}`} />
      ) : null,
    next: isPaginationValid(content, nextPage) ? (
      <link rel="next" href={`${contentUrlPath}?page=${nextPage}`} />
    ) : null,
    canonical: (
      <link
        rel="canonical"
        href={`${contentUrlPath}${
          currentPage > 1 ? `?page=${currentPage}` : ''
        }`}
      />
    )
  };

  return (
    <>
      <Helmet>
        {navigationMeta.prev}
        {navigationMeta.next}
        {navigationMeta.canonical}
      </Helmet>
      <article
        data-node-id={content.id}
        // eslint-disable-next-line react/no-unknown-property
        about={content.url.path}
        className="node node--type-hub-page vid-landing"
        css={[nodeTypeHubPage, vidLanding, styles.main]}
        ref={articleTag}
      >
        <SponsoredBar content={content} articleRef={articleTag} />

        {content.relatedHubPages && !content.hideRelatedTags && (
          <RelatedHubPages
            hubPages={content.relatedHubPages}
            backgroundColor={
              content.backgroundColor ? content.backgroundColor : 'white'
            }
            isAboveTheFold
          />
        )}
        {/*
        Keep this div to avoid a problem with React rehydration that leads
        to include the InfiniteScroll component inside the div with id "hub-page-first-page-content
        */}
        <div>
          {/*
          When loading a hub page with a pagination, execute an inline script before the body is rendered.
          Hide the div with the first pagination (the one rendered during gatsby build)
          to avoid a flash of content and CLS problems.
          */}
          {pageFromFirstRender !== 1 && (
            <script
              // eslint-disable-next-line
              dangerouslySetInnerHTML={{
                __html: `
                if (document.location.search.indexOf('page=') !== -1) {
                  document.head.insertAdjacentHTML("beforeend", '<style>#hub-page-first-page-content{display:none}</style>');
                }
                ;`
                  .replace(/\s+/gm, ' ')
                  .trim()
              }}
            />
          )}
          {showFirstPage && (
            <div id="hub-page-first-page-content" css={styles.firstPageContent}>
              {content.primaryTaxonomy?.entity?.vocabulary?.id ===
                'explorers' && (
                <div css={styles.explorers}>
                  <ContainerMain>
                    <PreHeadline>
                      <span>{t('Explorers Page')}</span>
                    </PreHeadline>
                  </ContainerMain>
                </div>
              )}
              {content.immersiveLead && (
                <ImmersiveLead
                  data={{ id: content.id, type: 'ImmersiveLead' }}
                  content={content}
                  isAboveTheFold
                />
              )}
              {content.mainContent && (
                <div className="node__cont" key={`node__cont_${content.id}`}>
                  {content.mainContent.map((currentParagraph, index) => {
                    const paragraph = currentParagraph;
                    if (!paragraph.entity) {
                      return null;
                    }
                    const key = `${paragraph.entity.type}_${index}_${paragraph.entity.id}`;
                    paragraph.entity.backgroundColor = content.backgroundColor
                      ? content.backgroundColor
                      : paragraph.entity.backgroundColor;
                    switch (paragraph.entity.type) {
                      case 'ParagraphFocusedHeroPackage':
                        return (
                          <FocusedHeroPackage
                            data={paragraph.entity}
                            content={content}
                            index={index}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphContentPackage':
                        return (
                          <ContentPackage
                            data={paragraph.entity}
                            content={content}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphLeftAndRightContentPackage':
                        return (
                          <LeftAndRightContentPackage
                            data={paragraph.entity}
                            content={content}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphTvGuideElement':
                        return <TvGuide data={paragraph.entity} key={key} />;
                      case 'ParagraphCallToActionButton':
                        return (
                          <CallToActionButton
                            url={paragraph.entity.ctaButtonLink.url?.path}
                            text={paragraph.entity.ctaButtonText}
                            openInNewTab={paragraph.entity.ctaOpenInNewTab}
                            key={key}
                          />
                        );
                      case 'ParagraphBannerModule':
                        return (
                          <BannerModule
                            data={paragraph.entity}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphBroadsheetModule':
                        return (
                          <Broadsheet
                            data={paragraph.entity}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphPhotoOfTheDay':
                        return (
                          <PhotoOfTheDayParagraph
                            data={paragraph.entity}
                            title={paragraph.entity.title}
                            imageEntity={
                              paragraph.entity.photoGallery?.entity?.firstImage
                                ?.entity?.[0]
                            }
                            header={paragraph.entity.header}
                            photographer={paragraph.entity.photoCredit}
                            galleryUrl={
                              paragraph.entity.photoGallery?.entity?.url?.path
                            }
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphContentHtmlPackage':
                        return (
                          <ContentHtmlPackage
                            data={paragraph.entity}
                            key={key}
                          />
                        );
                      case 'ParagraphCardstack':
                        return (
                          <CardStack
                            data={paragraph.entity}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphCarousel':
                        return (
                          <Carousel
                            data={paragraph.entity}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphInlineText':
                        return <InlineText data={paragraph.entity} key={key} />;
                      case 'ParagraphAmbientPlayerModule':
                        return (
                          <AmbientPlayerModule
                            data={paragraph.entity}
                            bundle={content.bundle}
                            key={key}
                          />
                        );
                      case 'ParagraphPromo':
                        return (
                          <PromoParagraph {...paragraph.entity} key={key} />
                        );
                      case 'ParagraphPromoBanner':
                        return (
                          <PromoBannerParagraph
                            {...paragraph.entity}
                            key={key}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                          />
                        );
                      case 'ParagraphArticleVideoPlaylist':
                        return (
                          <VideoPlaylist
                            data={paragraph.entity}
                            bundle={content.bundle}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                          />
                        );
                      case 'ParagraphStandaloneVideo':
                        return (
                          <StandaloneVideo
                            key={key}
                            data={paragraph.entity}
                            bundle={content.bundle}
                            content={content}
                            isFirst={false}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                          />
                        );
                      case 'ParagraphSweepstakes':
                        return (
                          <Sweepstakes key={key} data={paragraph.entity} />
                        );
                      case 'ParagraphNewsletterModule':
                        return (
                          <NewsletterModuleParagraph
                            data={paragraph.entity}
                            key={key}
                          />
                        );
                      case 'ParagraphDisneyPlus':
                        return (
                          <PromoCarousel
                            data={paragraph.entity}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                            type="disney_plus"
                          />
                        );
                      case 'ParagraphStarPlus':
                        return (
                          <PromoCarousel
                            data={paragraph.entity}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                            key={key}
                            type="star_plus"
                          />
                        );
                      case 'ParagraphImageCarousel':
                        return (
                          <ImageCarousel
                            key={key}
                            data={paragraph.entity}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                          />
                        );
                      case 'ParagraphOneUp':
                        return (
                          <OneUp
                            key={key}
                            data={paragraph.entity}
                            isAboveTheFold={
                              !content.immersiveLead && index === 0
                            }
                          />
                        );
                      case 'ParagraphPromoGrid':
                      case 'ParagraphManualPromoGrid':
                        return (
                          <PromoGridParagraph
                            data={paragraph.entity}
                            content={content}
                            key={key}
                          />
                        );
                      case 'ParagraphTravelGuideSearchBox':
                        return (
                          <TravelGuideSearchBoxParagraph
                            data={paragraph.entity}
                            content={content}
                            colors={colors}
                            key={key}
                          />
                        );
                      case 'ParagraphTravelGuideBreadcrumbs':
                        return (
                          <TravelGuideBreadcrumbsParagraph
                            data={paragraph.entity}
                            content={content}
                            colors={colors}
                            key={key}
                          />
                        );
                      case 'ParagraphTravelGuideCountryList':
                        return (
                          <TravelGuideCountryListParagraph
                            data={paragraph.entity}
                            content={content}
                            colors={colors}
                            key={key}
                          />
                        );
                      default:
                        break;
                    }
                    return null;
                  })}
                </div>
              )}

              {!content.hideDynamicContent &&
                content.termContent?.data?.featured &&
                content.termContent?.data?.featured.length > 0 && (
                  <TermView
                    data={content.termContent.data?.featured}
                    content={content}
                    page={1}
                    isAboveTheFold={
                      !content.mainContent || content.mainContent.length === 0
                    }
                  />
                )}
              <div>
                <SponsoredAdditionalText content={content} />
              </div>
              <AdInsert id={`hub_bottom__${content.id}`} content={content} />
            </div>
          )}
        </div>

        {!content.hideDynamicContent && (
          <InfiniteScroll
            content={content}
            onPaginationChange={memoizedSetCurrentPage}
          />
        )}
      </article>
    </>
  );
};

HubPage.propTypes = {
  content: PropTypes.objectOf(PropTypes.any).isRequired
};

export default WithLayout(HubPage);
